import { PhotoOutlined as Asset, InsertDriveFileOutlined as RecordIcon, Search } from '@mui/icons-material';
import { Box, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useProjectLink } from 'app/common/components/link/project-link/project-link-hook';
import Loading from 'app/common/components/loading';
import { Mutation, Query, Shape } from 'app/common/icons';
import type { PropsWithCallableChildren } from 'app/common/types';
import type { SearchResult } from 'app/universal-search/types';
import React, { type ReactNode } from 'react';

const iconMap: Record<string, JSX.Element> = {
  Asset: <Asset />,
  Shape: <Shape />,
  Query: <Query />,
  Mutation: <Mutation />
};

type SearchResultsItemProps = SearchResult & {
  readonly closeModal?: () => void;
};

function SearchResultsItem({ closeModal, href, type, summary }: SearchResultsItemProps) {
  href = useProjectLink({ href }).href;

  return (
    <ListItem onClick={closeModal} disablePadding>
      <ListItemButton href={href} data-testid="search-result-item">
        <ListItemIcon>{iconMap[type] ?? <RecordIcon />}</ListItemIcon>
        <ListItemText primary={summary} secondary={type} />
      </ListItemButton>
    </ListItem>
  );
}

type SearchResultsEmptyProps = { readonly query?: string };

function SearchResultsEmpty({ query }: SearchResultsEmptyProps) {
  return (
    <>
      <Box mb={2}>
        <Search fontSize="large" color="disabled" />
      </Box>
      <Typography variant="body2" color="textSecondary">
        No results{query ? <> for &ldquo;{query}&rdquo;</> : null}
      </Typography>
    </>
  );
}

type SearchResultsListProps = {
  results?: SearchResult[];
  emptyComponent: ReactNode;
};

function SearchResultsList({
  results,
  emptyComponent,
  children
}: PropsWithCallableChildren<SearchResultsListProps, SearchResult>) {
  const isEmpty = !results || results?.length === 0;

  return (
    <List
      component={isEmpty ? 'div' : 'ul'}
      sx={{
        height: '100%',
        overflowY: 'scroll',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isEmpty ? 'center' : 'inherit',
        alignItems: isEmpty ? 'center' : 'inherit',
        p: 0
      }}
    >
      {isEmpty
        ? emptyComponent
        : results.map((result, index) => (
            <React.Fragment key={result.href}>
              {children(result, index)}
              {index !== results.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
    </List>
  );
}

export type SearchResultsProps = Pick<SearchResultsItemProps, 'closeModal'> &
  Pick<SearchResultsListProps, 'results'> &
  SearchResultsEmptyProps & {
    readonly isLoading?: boolean;
  };

export function SearchResults({ query, results, isLoading, closeModal }: SearchResultsProps) {
  if (isLoading) {
    return <Loading />;
  }

  return (
    <SearchResultsList results={results} emptyComponent={<SearchResultsEmpty query={query} />}>
      {(result) => <SearchResultsItem closeModal={closeModal} {...result} />}
    </SearchResultsList>
  );
}

export default SearchResults;
